"use client";

import type React from "react";
import { AdminSidebar } from "@/components/admin/admin-sidebar";
import { TopBar } from "@/components/top-bar";
import { useContext, useEffect } from "react";
import { AppStateContext } from "@/components/context/AppContext";
import { useRouter } from "next/navigation";
import { teamAccess } from "@/lib/recruiterOffers";

export default function AdminLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const router = useRouter()
  const { userProfile, isUserFetching } = useContext(AppStateContext);

  // This area is TENANT team management: it lists and creates seats under the
  // signed-in owner. It is not global staff admin, so the gate asks "is this the
  // tenant owner of a tenant with seats", not "is this plan expensive enough".
  //
  // The old check was `plan.id < 4`, which is both too loose and too tight: the
  // new Recruiter plan takes a fresh id >= 4, so every paying customer passed,
  // while a legacy owner on a low id was redirected away from their own team.
  // See teamAccess() in lib/recruiterOffers.js for the owner-versus-seat rule.
  //
  // This is a UI gate only. Every endpoint behind it is authorised per uid on
  // the server, which stays the authority.
  useEffect(() => {
    // Wait for the profile to land: redirecting on an unresolved profile would
    // eject the owner on a cold load.
    if (isUserFetching || !userProfile) return
    if (!teamAccess(userProfile).canManageTeam) {
      router.push("/dashboard/agency")
    }
  }, [userProfile, isUserFetching, router])

  return (
    <div className="flex h-screen bg-background">
      <AdminSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
